import React, { useState, useEffect } from "react"

import { Button } from "../../../../components/shared/Buttons"
import { Input, Select, Toggle } from "../../../../components/shared/Inputs"
import Typography from "../../../../components/shared/Typography"
import { resolveBuilderPageSlug } from "../../utils/urlUtils"
import { useWebsiteBuilderV2 } from "../../WebsiteBuilderV2Context"

import { headerThemeCtaFields, resolveHeaderCtaService } from "./headerCtaUtils"

const HeaderForm = ({ theme, practice: practiceProp, onUpdate, onClose }) => {
  const { pages, practice: contextPractice } = useWebsiteBuilderV2()
  const currentPractice = contextPractice ?? practiceProp
  const defaultServicesSlug = resolveBuilderPageSlug(currentPractice, "services", "services")

  // Dynamic page options from context
  const pageOptions = pages
    ?.filter((page) => page.defaultPageType !== "home") // Exclude home page (by defaultPageType)
    ?.map((page) => ({
      value: page.slug,
      label: page.title
    })) || [
    { value: defaultServicesSlug, label: "Services" },
    { value: resolveBuilderPageSlug(currentPractice, "about", "about"), label: "About" },
    { value: resolveBuilderPageSlug(currentPractice, "contact", "contact"), label: "Contact" }
  ]

  const [ctaType, setCtaType] = useState(theme?.header?.ctaType || "page")
  const [buttonText, setButtonText] = useState(theme?.header?.buttonText || "Book Now")
  const [externalUrl, setExternalUrl] = useState(theme?.header?.externalUrl || "")
  // Find the correct page slug from stored value or use first available page
  const getInitialPage = () => {
    const storedPage = theme?.header?.page
    if (storedPage) {
      // Check if the stored page exists in current page options
      const matchingPage = pageOptions.find((option) => option.value === storedPage)
      if (matchingPage) {
        return storedPage
      }
    }
    // Fall back to first available page (Services)
    return pageOptions[0]?.value || defaultServicesSlug
  }

  const [page, setPage] = useState(getInitialPage())

  // Service options from practice (needed before resolving stored header CTA service)
  const serviceOptions =
    currentPractice?.services?.map((service) => ({
      value: service.id.toString(),
      label: service.name
    })) || []
  const serviceIds = serviceOptions.map((option) => option.value)

  // Header CTA service — separate from promo banner target (promoService)
  const [ctaService, setCtaService] = useState(() => resolveHeaderCtaService(theme?.header, serviceIds))

  // Promo banner fields
  const [showPromoBanner, setShowPromoBanner] = useState(theme?.header?.showPromoBanner || false)
  const [promoHeadline, setPromoHeadline] = useState(theme?.header?.promoHeadline || "")
  const [promoCtaType, setPromoCtaType] = useState(theme?.header?.promoCtaType || "service")
  const [promoButtonText, setPromoButtonText] = useState(theme?.header?.promoButtonText || "Learn more")
  const [promoService, setPromoService] = useState(theme?.header?.promoService || "")
  const [promoEvent, setPromoEvent] = useState(theme?.header?.promoEvent || "")
  const [promoExternalUrl, setPromoExternalUrl] = useState(theme?.header?.promoExternalUrl || "")
  const [promoBannerColorMode, setPromoBannerColorMode] = useState(theme?.header?.promoBannerColorMode || "white")
  const [showHome, setShowHome] = useState(theme?.header?.showHome || false)

  // Event options from practice (using bookableEventTypes only)
  const eventOptions =
    currentPractice?.bookableEventTypes?.map((eventType) => ({
      value: eventType.slug,
      label: eventType.name,
      url: `/events/${eventType.slug}` // Add the URL for the event type
    })) || []

  // Sync form state with theme prop when it changes
  useEffect(() => {
    setCtaType(theme?.header?.ctaType || "page")
    setButtonText(theme?.header?.buttonText || "Book Now")
    setExternalUrl(theme?.header?.externalUrl || "")

    // Handle page selection with proper mapping - only update if theme actually changed
    const storedPage = theme?.header?.page
    if (storedPage) {
      const matchingPage = pageOptions.find((option) => option.value === storedPage)
      if (matchingPage) {
        setPage(storedPage)
      } else {
        // If stored page doesn't match current options, use first available (Services)
        setPage(pageOptions[0]?.value || defaultServicesSlug)
      }
    } else {
      setPage(pageOptions[0]?.value || defaultServicesSlug)
    }

    setShowPromoBanner(theme?.header?.showPromoBanner || false)
    setPromoHeadline(theme?.header?.promoHeadline || "")
    setPromoCtaType(theme?.header?.promoCtaType || "service")
    setPromoButtonText(theme?.header?.promoButtonText || "Learn more")
    setPromoService(theme?.header?.promoService || "")
    setPromoEvent(theme?.header?.promoEvent || "")
    setPromoExternalUrl(theme?.header?.promoExternalUrl || "")
    setPromoBannerColorMode(theme?.header?.promoBannerColorMode || "white")
    setShowHome(theme?.header?.showHome || false)
    setCtaService(resolveHeaderCtaService(theme?.header, serviceIds))
  }, [theme]) // Removed pageOptions dependency to prevent overriding user selection

  // Handle initial page selection when pageOptions are first loaded
  useEffect(() => {
    if (!page) {
      setPage(pageOptions[0]?.value || defaultServicesSlug)
    }
  }, [page])

  const selectHeaderCtaType = (nextType) => {
    setCtaType(nextType)
    if (nextType === "page" && pageOptions.length > 0 && !page) {
      setPage(pageOptions[0].value)
    } else if (nextType === "service" && serviceOptions.length > 0 && !ctaService) {
      setCtaService(serviceOptions[0].value)
    }
  }

  const selectPromoCtaType = (nextType) => {
    setPromoCtaType(nextType)
    if (nextType === "page" && pageOptions.length > 0) {
      setPromoService(pageOptions[0].value)
    } else if (nextType === "service" && serviceOptions.length > 0) {
      setPromoService(serviceOptions[0].value)
    } else if (nextType === "event" && eventOptions.length > 0) {
      setPromoEvent(eventOptions[0].value)
    }
  }

  const ctaFields = headerThemeCtaFields({ ctaType, ctaService, page, externalUrl })

  // Auto-select first event when switching to "event" type and there's only one event
  useEffect(() => {
    if (promoCtaType === "event" && eventOptions.length === 1 && !promoEvent) {
      setPromoEvent(eventOptions[0].value)
    }
  }, [promoCtaType, eventOptions, promoEvent])

  // Apply temporary preview when values change (logo is set in Sitewide styles → Logo)
  useEffect(() => {
    const headerData = {
      header: {
        ...theme?.header,
        ctaType,
        buttonText,
        page,
        showPromoBanner,
        promoHeadline,
        promoCtaType,
        promoButtonText,
        promoService,
        promoEvent,
        promoExternalUrl,
        promoBannerColorMode,
        showHome,
        externalUrl,
        ...ctaFields
      }
    }
    onUpdate(headerData, { temporary: true })
  }, [
    ctaType,
    ctaService,
    buttonText,
    page,
    showPromoBanner,
    promoHeadline,
    promoCtaType,
    promoButtonText,
    promoService,
    promoEvent,
    promoExternalUrl,
    promoBannerColorMode,
    showHome,
    externalUrl
  ])

  const handleSave = () => {
    const saveData = {
      header: {
        ...theme?.header,
        ctaType,
        buttonText,
        page,
        showPromoBanner,
        promoHeadline,
        promoCtaType,
        promoButtonText,
        promoService,
        promoEvent,
        promoExternalUrl,
        promoBannerColorMode,
        showHome,
        externalUrl,
        ...ctaFields
      }
    }

    onUpdate(saveData)
    onClose()
  }

  const handleCancelClick = () => {
    // Revert to stored theme when closing without saving
    const storedCtaType = theme?.header?.ctaType || "page"
    const storedPage = theme?.header?.page || pageOptions[0]?.value || defaultServicesSlug
    const storedPromoService = theme?.header?.promoService || ""
    const storedPromoEvent = theme?.header?.promoEvent || ""
    const storedPromoExternalUrl = theme?.header?.promoExternalUrl || ""
    const storedExternalUrl = theme?.header?.externalUrl || ""
    const storedCtaFields = headerThemeCtaFields({
      ctaType: storedCtaType,
      ctaService: resolveHeaderCtaService(theme?.header, serviceIds),
      page: storedPage,
      externalUrl: storedExternalUrl
    })

    const storedHeaderData = {
      header: {
        ...theme?.header,
        ctaType: storedCtaType,
        buttonText: theme?.header?.buttonText || "Book Now",
        page: storedPage,
        showPromoBanner: theme?.header?.showPromoBanner || false,
        promoHeadline: theme?.header?.promoHeadline || "",
        promoCtaType: theme?.header?.promoCtaType || "service",
        promoButtonText: theme?.header?.promoButtonText || "Learn more",
        promoService: storedPromoService,
        promoEvent: storedPromoEvent,
        promoExternalUrl: storedPromoExternalUrl,
        promoBannerColorMode: theme?.header?.promoBannerColorMode || "white",
        showHome: theme?.header?.showHome || false,
        externalUrl: storedExternalUrl,
        ...storedCtaFields
      }
    }
    onUpdate(storedHeaderData, { temporary: true })
    onClose()
  }

  return (
    <div className="w-full max-w-lg rounded-xl bg-white shadow-xl">
      {/* Logo is set in Sitewide styles → Logo */}
      <div className="px-8 pt-6">
        <Typography variant="small" className="text-gray">
          The logo shown in the header is set in <strong>Sitewide styles → Logo</strong>. Edit it there to change the
          image or text logo for both header and footer.
        </Typography>
      </div>

      {/* Navigation Settings */}
      <div className="px-8 pb-8">
        <div className="mb-6 flex flex-col gap-2">
          <Typography variant="h5" className="font-bold text-gray-dark">
            Include &quot;Home&quot; link in navigation
          </Typography>
          <Typography variant="small" className="text-gray">
            Show the home link in your website header navigation.
          </Typography>
        </div>
        <div className="flex items-center gap-3">
          <Toggle checked={showHome} onChange={setShowHome} />
          <Typography variant="body" className="font-normal text-gray-dark">
            Show Home Link
          </Typography>
        </div>
      </div>

      {/* CTA Button Section */}
      <div className="px-8 pb-8">
        <Typography variant="h5" className="mb-1 font-bold text-gray-dark">
          Call-to-action button
        </Typography>
        <Typography variant="small" className="mb-4 text-gray">
          Guide visitors to the next step with a clear call-to-action button.
        </Typography>
        <div className="mb-4 flex items-center space-x-6">
          <label className="flex items-center space-x-2">
            <input
              type="radio"
              name="ctaType"
              value="page"
              checked={ctaType === "page"}
              onChange={() => selectHeaderCtaType("page")}
              className="size-5 border-gray-300 text-teal form-radio focus:ring-teal"
            />
            <span className="font-medium text-gray-dark">Page</span>
          </label>
          <label className="flex items-center space-x-2">
            <input
              type="radio"
              name="ctaType"
              value="service"
              checked={ctaType === "service"}
              onChange={() => selectHeaderCtaType("service")}
              className="size-5 border-gray-300 text-teal form-radio focus:ring-teal"
            />
            <span className="font-medium text-gray-dark">Service</span>
          </label>
          <label className="flex items-center space-x-2">
            <input
              type="radio"
              name="ctaType"
              value="external"
              checked={ctaType === "external"}
              onChange={() => selectHeaderCtaType("external")}
              className="size-5 border-gray-300 text-teal form-radio focus:ring-teal"
            />
            <span className="font-medium text-gray-dark">External Link</span>
          </label>
        </div>
        <Input
          label="Button text"
          value={buttonText}
          onChange={(e) => setButtonText(e.target.value)}
          className="mb-4"
        />
        {ctaType === "page" ? (
          <Select label="Page" value={page} onChange={(e) => setPage(e.target.value)} options={pageOptions} />
        ) : ctaType === "service" ? (
          <Select
            label="Service"
            value={ctaService}
            onChange={(e) => setCtaService(e.target.value)}
            options={serviceOptions}
            defaultOption="Select a service"
          />
        ) : (
          <Input
            label="External URL"
            type="url"
            value={externalUrl}
            onChange={(e) => setExternalUrl(e.target.value)}
            placeholder="https://example.com"
            className="mb-4"
          />
        )}
      </div>

      {/* Promo Banner Section */}
      <div className="px-8 pb-8">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex-1">
            <Typography variant="h5" className="mb-1 font-bold text-gray-dark">
              Promo Banner
            </Typography>
            <Typography variant="small" className="text-gray">
              Add a small promo banner above the header that is visible on all pages. Use this to promote a specific
              service, package, event, or any action you want to promote.
            </Typography>
          </div>
          <div className="ml-4">
            <Toggle checked={showPromoBanner} onChange={setShowPromoBanner} />
          </div>
        </div>

        {showPromoBanner && (
          <div className="space-y-6">
            {/* Promo Headline */}
            <div>
              <Typography variant="h5" className="mb-1 font-bold text-gray-dark">
                Promo headline
              </Typography>
              <Typography variant="small" className="mb-3 text-gray">
                Describe your promotion in a short headline.
              </Typography>
              <textarea
                value={promoHeadline}
                onChange={(e) => setPromoHeadline(e.target.value)}
                placeholder="Enter your promo headline..."
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal focus:outline-none focus:ring-1 focus:ring-teal"
                rows={3}
              />
            </div>

            {/* Promo Banner Color Mode */}
            <div>
              <Typography variant="h5" className="mb-1 font-bold text-gray-dark">
                Background color
              </Typography>
              <Typography variant="small" className="mb-3 text-gray">
                Choose the background color style for your promo banner.
              </Typography>
              <div className="flex flex-row gap-4">
                {[
                  { id: "white", label: "White", color: "#FFFFFF" },
                  { id: "light", label: "Light", color: theme?.colors?.bgLight || "#F4ECE9" },
                  { id: "dark", label: "Dark", color: theme?.colors?.primary || "#BD654F" }
                ].map((option) => (
                  <label
                    key={option.id}
                    className={`
                      flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 p-3 transition-all
                      ${
                        promoBannerColorMode === option.id
                          ? "border-teal-500 bg-teal-50"
                          : "border-gray-200 hover:border-gray-300"
                      }
                    `}
                    onClick={() => setPromoBannerColorMode(option.id)}>
                    <input
                      type="radio"
                      name="promoBannerColorMode"
                      value={option.id}
                      checked={promoBannerColorMode === option.id}
                      onChange={() => setPromoBannerColorMode(option.id)}
                      className="sr-only"
                    />
                    <div
                      className="size-8 rounded-full border border-gray-300 shadow-sm"
                      style={{ backgroundColor: option.color }}
                    />
                    <span
                      className={`text-sm font-medium ${
                        promoBannerColorMode === option.id ? "text-teal-700" : "text-gray-700"
                      }`}>
                      {option.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Promo CTA Section */}
            <div>
              <Typography variant="h5" className="mb-1 font-bold text-gray-dark">
                Promo call-to-action link
              </Typography>
              <Typography variant="small" className="mb-4 text-gray">
                Guide visitors to the next step with a clear call-to-action button.
              </Typography>

              {/* CTA Type Radio Buttons */}
              <div className="mb-4 flex items-center space-x-6">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="promoCtaType"
                    value="page"
                    checked={promoCtaType === "page"}
                    onChange={() => selectPromoCtaType("page")}
                    className="size-5 border-gray-300 text-teal form-radio focus:ring-teal"
                  />
                  <span className="font-medium text-gray-dark">Page</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="promoCtaType"
                    value="service"
                    checked={promoCtaType === "service"}
                    onChange={() => selectPromoCtaType("service")}
                    className="size-5 border-gray-300 text-teal form-radio focus:ring-teal"
                  />
                  <span className="font-medium text-gray-dark">Service</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="promoCtaType"
                    value="event"
                    checked={promoCtaType === "event"}
                    onChange={() => selectPromoCtaType("event")}
                    className="size-5 border-gray-300 text-teal form-radio focus:ring-teal"
                  />
                  <span className="font-medium text-gray-dark">Events</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="promoCtaType"
                    value="external"
                    checked={promoCtaType === "external"}
                    onChange={() => selectPromoCtaType("external")}
                    className="size-5 border-gray-300 text-teal form-radio focus:ring-teal"
                  />
                  <span className="font-medium text-gray-dark">External Link</span>
                </label>
              </div>

              {/* Button Text */}
              <Input
                label="Button text"
                value={promoButtonText}
                onChange={(e) => setPromoButtonText(e.target.value)}
                className="mb-4"
              />

              {/* Page Selection (only when page type is selected) */}
              {promoCtaType === "page" && (
                <Select
                  label="Page"
                  value={promoService}
                  onChange={(e) => setPromoService(e.target.value)}
                  options={pageOptions}
                  placeholder="Select a page"
                />
              )}

              {/* Service Selection (only when service type is selected) */}
              {promoCtaType === "service" && (
                <div>
                  <Select
                    label="Service"
                    value={promoService}
                    onChange={(e) => setPromoService(e.target.value)}
                    options={serviceOptions}
                    placeholder="Select a service"
                  />
                  <Typography variant="small" className="mt-2 text-gray">
                    Go to your Services to add additional options.
                  </Typography>
                </div>
              )}

              {/* Event Selection (only when event type is selected) */}
              {promoCtaType === "event" && (
                <div>
                  <Select
                    label="Event"
                    value={promoEvent}
                    onChange={(e) => setPromoEvent(e.target.value)}
                    options={eventOptions}
                    placeholder={eventOptions.length > 0 ? "Select an event" : "No events available"}
                  />
                  <Typography variant="small" className="mt-2 text-gray">
                    {eventOptions.length > 0
                      ? "Go to your Events to add additional options."
                      : "No events found. Go to your Events section to create event types."}
                  </Typography>
                </div>
              )}

              {/* External URL (only when external type is selected) */}
              {promoCtaType === "external" && (
                <Input
                  label="External URL"
                  type="url"
                  value={promoExternalUrl}
                  onChange={(e) => setPromoExternalUrl(e.target.value)}
                  placeholder="https://example.com"
                  className="mb-4"
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer Buttons */}
      <div className="sticky bottom-0 z-10 flex items-center justify-end space-x-3 border-t border-gray-light bg-white px-8 py-6">
        <Button type="secondary" onClick={handleCancelClick} className="px-6">
          Close
        </Button>
        <Button type="primary" onClick={handleSave} className="px-6">
          Save
        </Button>
      </div>
    </div>
  )
}

export default HeaderForm
